import { ADS_OBJECT_TYPES } from "../../../utils/constants";

/**
 * Maps an error path to entity information and navigation payload
 *
 * @param {string} errorPath - The error path (e.g., "assetsGroups.[0].titles.[2].value")
 * @param {Function} getValues - Function to get form values
 * @returns {Object|null} Object with entity type, name, and navigation payload, or null if cannot resolve
 */
export const mapErrorToEntity = (errorPath, getValues) => {
  if (!errorPath || !getValues) return null;

  const values = getValues();
  const pathParts = errorPath.split(".");

  // Handle onboarding errors (language, locations)
  if (errorPath === "language" || errorPath === "locations") {
    return {
      entityType: "onboarding",
      entityName: errorPath === "language" ? "Language" : "Locations",
      navigationPayload: {
        platform: undefined,
        assetsGroupID: undefined,
        adGroupID: undefined,
        adID: undefined,
        from: "addPlatforms",
      },
    };
  }

  // Handle campaign/platform errors: platformsData.{platform}
  if (pathParts[0] === "platformsData" && pathParts.length >= 2) {
    const platform = pathParts[1];
    const platformData = values?.platformsData?.[platform];
    const name = platformData?.name || platform;

    return {
      entityType: ADS_OBJECT_TYPES.campaign,
      entityName: name,
      navigationPayload: {
        platform,
        assetsGroupID: undefined,
        adGroupID: undefined,
        adID: undefined,
      },
    };
  }

  // Handle assetsGroup errors: assetsGroups.[i]
  if (pathParts[0] === "assetsGroups" && pathParts.length >= 2) {
    const indexMatch = pathParts[1].match(/\[(\d+)\]/);
    if (indexMatch) {
      const index = parseInt(indexMatch[1], 10);
      const assetsGroup = values?.assetsGroups?.[index];

      if (assetsGroup) {
        return {
          entityType: ADS_OBJECT_TYPES.assetsGroup,
          entityName: assetsGroup.name || `Assets Group ${index + 1}`,
          navigationPayload: {
            platform: assetsGroup.platform,
            assetsGroupID: assetsGroup.id,
            adGroupID: undefined,
            adID: undefined,
          },
        };
      }
    }
  }

  // Handle adGroup errors: adsGroups.[i]
  if (pathParts[0] === "adsGroups" && pathParts.length >= 2) {
    const indexMatch = pathParts[1].match(/\[(\d+)\]/);
    if (indexMatch) {
      const index = parseInt(indexMatch[1], 10);
      const adGroup = values?.adsGroups?.[index];

      if (adGroup) {
        // Check if error is in ads array: adsGroups.[i].ads.[j]
        if (pathParts.length >= 3 && pathParts[2] === "ads") {
          const adIndexMatch = pathParts[3]?.match(/\[(\d+)\]/);
          if (adIndexMatch) {
            const adIndex = parseInt(adIndexMatch[1], 10);
            const ad = adGroup?.ads?.[adIndex];

            if (ad) {
              return {
                entityType: ADS_OBJECT_TYPES.ad,
                entityName: ad.name || `Ad ${adIndex + 1}`,
                navigationPayload: {
                  platform: adGroup.platform,
                  assetsGroupID: undefined,
                  adGroupID: adGroup.id,
                  adID: ad.id,
                },
              };
            }
          }
        }

        // Error is in adGroup itself
        return {
          entityType: ADS_OBJECT_TYPES.adGroup,
          entityName: adGroup.name || `Ad Group ${index + 1}`,
          navigationPayload: {
            platform: adGroup.platform,
            assetsGroupID: undefined,
            adGroupID: adGroup.id,
            adID: undefined,
          },
        };
      }
    }
  }

  // If we can't map it, return null
  return null;
};
